import { useState, useCallback, type FC } from 'react';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface BodyMapProps {
  variant: 'body-front' | 'body-back' | 'face';
  selectedZones: string[];
  onZoneToggle: (zoneId: string) => void;
  gender?: 'F' | 'M';
  readOnly?: boolean;
  /**
   * Zone custom da disegnare sopra l'outline del corpo.
   * Se passata, sostituisce le zone di default e disabilita il toggle
   * front/back (la variant scelta determina solo l'outline). Usato dal
   * BodyMapField per parametrizzare le zone selezionabili in base alle
   * options del form_field DB (es. cellulite_zone mostra solo Glutei,
   * Cosce, Interno coscia).
   */
  zones?: ZoneDefinition[];
}

export interface ZoneDefinition {
  id: string;
  label: string;
  path: string;
}

/* --------------------------------------------------------------------------
   Zone label maps (Italian)
   -------------------------------------------------------------------------- */

const ZONE_LABELS: Record<string, string> = {
  // Body front
  viso: 'Viso',
  collo: 'Collo',
  spalla_dx: 'Spalla dx',
  spalla_sx: 'Spalla sx',
  petto: 'Petto',
  braccio_dx: 'Braccio dx',
  braccio_sx: 'Braccio sx',
  avambraccio_dx: 'Avambraccio dx',
  avambraccio_sx: 'Avambraccio sx',
  addome: 'Addome',
  fianco_dx: 'Fianco dx',
  fianco_sx: 'Fianco sx',
  inguine: 'Inguine',
  coscia_dx: 'Coscia dx',
  coscia_sx: 'Coscia sx',
  ginocchio_dx: 'Ginocchio dx',
  ginocchio_sx: 'Ginocchio sx',
  polpaccio_dx: 'Polpaccio dx',
  polpaccio_sx: 'Polpaccio sx',
  caviglia_dx: 'Caviglia dx',
  caviglia_sx: 'Caviglia sx',
  // Body back
  nuca: 'Nuca',
  spalla_post_dx: 'Spalla post. dx',
  spalla_post_sx: 'Spalla post. sx',
  schiena_alta: 'Schiena alta',
  schiena_bassa: 'Schiena bassa',
  gluteo_dx: 'Gluteo dx',
  gluteo_sx: 'Gluteo sx',
  coscia_post_dx: 'Coscia post. dx',
  coscia_post_sx: 'Coscia post. sx',
  polpaccio_post_dx: 'Polpaccio post. dx',
  polpaccio_post_sx: 'Polpaccio post. sx',
  // Face
  fronte: 'Fronte',
  tempia_dx: 'Tempia dx',
  tempia_sx: 'Tempia sx',
  occhio_dx: 'Occhio dx',
  occhio_sx: 'Occhio sx',
  naso: 'Naso',
  guancia_dx: 'Guancia dx',
  guancia_sx: 'Guancia sx',
  labbra: 'Labbra',
  mento: 'Mento',
  mascella_dx: 'Mascella dx',
  mascella_sx: 'Mascella sx',
};

/* --------------------------------------------------------------------------
   SVG Zone Paths — Body Front (Female silhouette)
   ViewBox: 0 0 200 500
   -------------------------------------------------------------------------- */

const BODY_FRONT_ZONES: ZoneDefinition[] = [
  {
    id: 'viso',
    label: 'Viso',
    path: 'M88,18 C88,8 92,2 100,2 C108,2 112,8 112,18 L112,34 C112,42 108,48 100,48 C92,48 88,42 88,34 Z',
  },
  {
    id: 'collo',
    label: 'Collo',
    path: 'M94,48 L106,48 L108,66 L92,66 Z',
  },
  {
    id: 'spalla_dx',
    label: 'Spalla dx',
    path: 'M108,66 L130,70 L134,82 L118,86 L108,78 Z',
  },
  {
    id: 'spalla_sx',
    label: 'Spalla sx',
    path: 'M92,66 L70,70 L66,82 L82,86 L92,78 Z',
  },
  {
    id: 'petto',
    label: 'Petto',
    path: 'M82,78 L118,78 L120,118 L80,118 Z',
  },
  {
    id: 'braccio_dx',
    label: 'Braccio dx',
    path: 'M134,82 L142,84 L146,140 L136,142 L130,118 L118,86 Z',
  },
  {
    id: 'braccio_sx',
    label: 'Braccio sx',
    path: 'M66,82 L58,84 L54,140 L64,142 L70,118 L82,86 Z',
  },
  {
    id: 'avambraccio_dx',
    label: 'Avambraccio dx',
    path: 'M136,142 L146,140 L150,200 L140,202 Z',
  },
  {
    id: 'avambraccio_sx',
    label: 'Avambraccio sx',
    path: 'M64,142 L54,140 L50,200 L60,202 Z',
  },
  {
    id: 'addome',
    label: 'Addome',
    path: 'M80,118 L120,118 L122,170 L78,170 Z',
  },
  {
    id: 'fianco_dx',
    label: 'Fianco dx',
    path: 'M120,118 L130,118 L132,170 L122,170 Z',
  },
  {
    id: 'fianco_sx',
    label: 'Fianco sx',
    path: 'M80,118 L70,118 L68,170 L78,170 Z',
  },
  {
    id: 'inguine',
    label: 'Inguine',
    path: 'M84,170 L116,170 L112,200 L100,210 L88,200 Z',
  },
  {
    id: 'coscia_dx',
    label: 'Coscia dx',
    path: 'M112,200 L132,170 L134,270 L114,270 L108,240 Z',
  },
  {
    id: 'coscia_sx',
    label: 'Coscia sx',
    path: 'M88,200 L68,170 L66,270 L86,270 L92,240 Z',
  },
  {
    id: 'ginocchio_dx',
    label: 'Ginocchio dx',
    path: 'M114,270 L134,270 L132,300 L112,300 Z',
  },
  {
    id: 'ginocchio_sx',
    label: 'Ginocchio sx',
    path: 'M86,270 L66,270 L68,300 L88,300 Z',
  },
  {
    id: 'polpaccio_dx',
    label: 'Polpaccio dx',
    path: 'M112,300 L132,300 L128,390 L114,390 Z',
  },
  {
    id: 'polpaccio_sx',
    label: 'Polpaccio sx',
    path: 'M88,300 L68,300 L72,390 L86,390 Z',
  },
  {
    id: 'caviglia_dx',
    label: 'Caviglia dx',
    path: 'M114,390 L128,390 L126,420 L134,430 L110,430 L112,420 Z',
  },
  {
    id: 'caviglia_sx',
    label: 'Caviglia sx',
    path: 'M86,390 L72,390 L74,420 L66,430 L90,430 L88,420 Z',
  },
];

/* --------------------------------------------------------------------------
   SVG Zone Paths — Body Back (Female silhouette)
   ViewBox: 0 0 200 500
   -------------------------------------------------------------------------- */

const BODY_BACK_ZONES: ZoneDefinition[] = [
  {
    id: 'nuca',
    label: 'Nuca',
    path: 'M90,30 C90,10 95,2 100,2 C105,2 110,10 110,30 L110,48 C110,55 106,60 100,60 C94,60 90,55 90,48 Z',
  },
  {
    id: 'spalla_post_dx',
    label: 'Spalla post. dx',
    path: 'M110,66 L132,72 L136,86 L120,90 L110,80 Z',
  },
  {
    id: 'spalla_post_sx',
    label: 'Spalla post. sx',
    path: 'M90,66 L68,72 L64,86 L80,90 L90,80 Z',
  },
  {
    id: 'schiena_alta',
    label: 'Schiena alta',
    path: 'M80,80 L120,80 L122,140 L78,140 Z',
  },
  {
    id: 'schiena_bassa',
    label: 'Schiena bassa',
    path: 'M78,140 L122,140 L124,185 L76,185 Z',
  },
  {
    id: 'gluteo_dx',
    label: 'Gluteo dx',
    path: 'M100,185 L130,185 L132,220 L112,230 L100,220 Z',
  },
  {
    id: 'gluteo_sx',
    label: 'Gluteo sx',
    path: 'M100,185 L70,185 L68,220 L88,230 L100,220 Z',
  },
  {
    id: 'coscia_post_dx',
    label: 'Coscia post. dx',
    path: 'M112,230 L134,220 L132,300 L112,300 Z',
  },
  {
    id: 'coscia_post_sx',
    label: 'Coscia post. sx',
    path: 'M88,230 L66,220 L68,300 L88,300 Z',
  },
  {
    id: 'polpaccio_post_dx',
    label: 'Polpaccio post. dx',
    path: 'M112,300 L132,300 L128,400 L114,400 Z',
  },
  {
    id: 'polpaccio_post_sx',
    label: 'Polpaccio post. sx',
    path: 'M88,300 L68,300 L72,400 L86,400 Z',
  },
];

/* --------------------------------------------------------------------------
   SVG Zone Paths — Face
   ViewBox: 0 0 200 260
   -------------------------------------------------------------------------- */

const FACE_ZONES: ZoneDefinition[] = [
  {
    id: 'fronte',
    label: 'Fronte',
    path: 'M55,40 C55,15 70,2 100,2 C130,2 145,15 145,40 L145,72 L55,72 Z',
  },
  {
    id: 'tempia_dx',
    label: 'Tempia dx',
    path: 'M145,40 L165,50 L162,90 L145,85 L145,40 Z',
  },
  {
    id: 'tempia_sx',
    label: 'Tempia sx',
    path: 'M55,40 L35,50 L38,90 L55,85 L55,40 Z',
  },
  {
    id: 'occhio_dx',
    label: 'Occhio dx',
    path: 'M108,72 L140,72 L140,100 L108,100 Z',
  },
  {
    id: 'occhio_sx',
    label: 'Occhio sx',
    path: 'M60,72 L92,72 L92,100 L60,100 Z',
  },
  {
    id: 'naso',
    label: 'Naso',
    path: 'M88,85 L112,85 L114,145 L100,152 L86,145 Z',
  },
  {
    id: 'guancia_dx',
    label: 'Guancia dx',
    path: 'M114,100 L155,95 L150,160 L120,165 L114,145 Z',
  },
  {
    id: 'guancia_sx',
    label: 'Guancia sx',
    path: 'M86,100 L45,95 L50,160 L80,165 L86,145 Z',
  },
  {
    id: 'labbra',
    label: 'Labbra',
    path: 'M80,152 L120,152 L118,180 L100,186 L82,180 Z',
  },
  {
    id: 'mento',
    label: 'Mento',
    path: 'M82,180 L118,180 L114,215 L100,225 L86,215 Z',
  },
  {
    id: 'mascella_dx',
    label: 'Mascella dx',
    path: 'M118,165 L150,160 L145,210 L114,215 L118,180 Z',
  },
  {
    id: 'mascella_sx',
    label: 'Mascella sx',
    path: 'M82,165 L50,160 L55,210 L86,215 L82,180 Z',
  },
];

/* --------------------------------------------------------------------------
   Background silhouette outlines
   -------------------------------------------------------------------------- */

const BODY_FRONT_OUTLINE =
  'M100,2 C108,2 114,8 114,20 L114,34 C114,44 110,50 106,52 L108,66 L132,72 L142,84 L150,200 L140,202 L136,142 L132,170 L134,270 L132,300 L128,390 L134,430 L110,430 L112,300 L108,240 L100,210 L92,240 L88,300 L90,430 L66,430 L72,390 L68,300 L66,270 L68,170 L64,142 L60,202 L50,200 L58,84 L68,72 L92,66 L94,52 C90,50 86,44 86,34 L86,20 C86,8 92,2 100,2 Z';

const BODY_BACK_OUTLINE =
  'M100,2 C106,2 112,10 112,30 L112,48 C112,58 108,64 106,66 L110,66 L134,72 L142,86 L150,200 L140,202 L136,142 L132,220 L134,300 L128,400 L130,430 L110,430 L112,300 L112,230 L100,220 L88,230 L88,300 L90,430 L70,430 L72,400 L68,300 L66,220 L64,142 L60,202 L50,200 L58,86 L66,72 L90,66 L94,66 C92,64 88,58 88,48 L88,30 C88,10 94,2 100,2 Z';

const FACE_OUTLINE =
  'M100,2 C135,2 160,25 165,50 L162,90 L155,95 L150,160 L145,210 L114,215 L100,225 L86,215 L55,210 L50,160 L45,95 L38,90 L35,50 C40,25 65,2 100,2 Z';

/* --------------------------------------------------------------------------
   CSS for pulse animation (injected via style tag in component)
   -------------------------------------------------------------------------- */

const PULSE_KEYFRAMES = `
@keyframes bodymap-zone-pulse {
  0% { opacity: 0.55; }
  50% { opacity: 0.75; }
  100% { opacity: 0.55; }
}
`;

/* --------------------------------------------------------------------------
   Helper: get zones for variant
   -------------------------------------------------------------------------- */

function getZonesForVariant(variant: BodyMapProps['variant']): ZoneDefinition[] {
  switch (variant) {
    case 'body-front':
      return BODY_FRONT_ZONES;
    case 'body-back':
      return BODY_BACK_ZONES;
    case 'face':
      return FACE_ZONES;
  }
}

function getOutlineForVariant(variant: BodyMapProps['variant']): string {
  switch (variant) {
    case 'body-front':
      return BODY_FRONT_OUTLINE;
    case 'body-back':
      return BODY_BACK_OUTLINE;
    case 'face':
      return FACE_OUTLINE;
  }
}

function getViewBoxForVariant(variant: BodyMapProps['variant']): string {
  return variant === 'face' ? '0 0 200 260' : '0 0 200 440';
}

/* --------------------------------------------------------------------------
   BodyMap Component
   -------------------------------------------------------------------------- */

export const BodyMap: FC<BodyMapProps> = ({
  variant,
  selectedZones,
  onZoneToggle,
  readOnly = false,
  zones: customZones,
}) => {
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const [currentVariant, setCurrentVariant] = useState(variant);

  // Quando l'utente passa zones custom uso quelle, altrimenti uso le zone
  // di default per la variant. Il toggle front/back resta disponibile solo
  // in modalita' default (custom = singola vista parametrizzata).
  const zones = customZones ?? getZonesForVariant(currentVariant);
  const outline = getOutlineForVariant(currentVariant);
  const viewBox = getViewBoxForVariant(currentVariant);
  const isCustomMode = customZones !== undefined;

  const handleZoneClick = useCallback(
    (zoneId: string) => {
      if (readOnly) return;
      onZoneToggle(zoneId);
    },
    [readOnly, onZoneToggle],
  );

  const handleZoneKeyDown = useCallback(
    (e: React.KeyboardEvent, zoneId: string) => {
      if (readOnly) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onZoneToggle(zoneId);
      }
    },
    [readOnly, onZoneToggle],
  );

  const isBodyVariant = variant === 'body-front' || variant === 'body-back';
  const canToggleView = isBodyVariant && !isCustomMode;

  const handleToggleView = useCallback(() => {
    setCurrentVariant((prev) =>
      prev === 'body-front' ? 'body-back' : 'body-front',
    );
  }, []);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Inject keyframes */}
      <style dangerouslySetInnerHTML={{ __html: PULSE_KEYFRAMES }} />

      {/* Toggle front/back button */}
      {canToggleView && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleToggleView}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2',
              'rounded-[var(--radius-full)]',
              'border border-border-default bg-surface-card',
              'text-sm font-semibold font-[family-name:var(--font-heading)]',
              'text-text-secondary',
              'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
              'hover:border-border-hover hover:bg-surface-sunken',
              'active:scale-[0.97]',
            )}
            aria-label={
              currentVariant === 'body-front'
                ? 'Mostra vista posteriore'
                : 'Mostra vista frontale'
            }
          >
            {/* Flip icon */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17 1l4 4-4 4" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <path d="M7 23l-4-4 4-4" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
            {currentVariant === 'body-front' ? 'Vista posteriore' : 'Vista frontale'}
          </button>
        </div>
      )}

      {/* SVG Body Map */}
      <div
        className={cn(
          'relative w-full max-w-[320px]',
          'transition-opacity duration-[var(--duration-normal)] ease-[var(--ease-out)]',
        )}
        role="group"
        aria-label={
          currentVariant === 'body-front'
            ? 'Mappa corporea frontale'
            : currentVariant === 'body-back'
              ? 'Mappa corporea posteriore'
              : 'Mappa del viso'
        }
      >
        <svg
          viewBox={viewBox}
          className="w-full h-auto"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Background silhouette outline */}
          <path
            d={outline}
            fill="none"
            stroke="var(--color-border-default)"
            strokeWidth="1.2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Interactive zones */}
          {zones.map((zone) => {
            const isSelected = selectedZones.includes(zone.id);
            const isHovered = hoveredZone === zone.id;

            return (
              <path
                key={zone.id}
                d={zone.path}
                fill={
                  isSelected
                    ? 'var(--color-accent-primary)'
                    : isHovered && !readOnly
                      ? 'var(--color-accent-primary-light)'
                      : 'transparent'
                }
                stroke={
                  isSelected
                    ? 'var(--color-accent-primary-active)'
                    : isHovered && !readOnly
                      ? 'var(--color-accent-primary)'
                      : 'var(--color-border-default)'
                }
                strokeWidth={isSelected || isHovered ? '1.5' : '0.8'}
                strokeLinejoin="round"
                opacity={isSelected ? undefined : 1}
                style={
                  isSelected
                    ? {
                        animation: 'bodymap-zone-pulse 2s ease-in-out infinite',
                        cursor: readOnly ? 'default' : 'pointer',
                      }
                    : {
                        cursor: readOnly ? 'default' : 'pointer',
                        transition:
                          'fill 250ms cubic-bezier(0.16,1,0.3,1), stroke 250ms cubic-bezier(0.16,1,0.3,1), opacity 250ms cubic-bezier(0.16,1,0.3,1)',
                      }
                }
                onClick={() => handleZoneClick(zone.id)}
                onMouseEnter={() => setHoveredZone(zone.id)}
                onMouseLeave={() => setHoveredZone(null)}
                onKeyDown={(e) => handleZoneKeyDown(e, zone.id)}
                tabIndex={readOnly ? -1 : 0}
                role="button"
                aria-pressed={isSelected}
                aria-label={zone.label}
              >
                <title>{zone.label}</title>
              </path>
            );
          })}
        </svg>

        {/* Hovered zone tooltip */}
        {hoveredZone && !readOnly && (
          <div
            className={cn(
              'absolute top-2 left-1/2 -translate-x-1/2',
              'px-3 py-1.5 rounded-[var(--radius-full)]',
              'bg-text-primary text-text-inverted',
              'text-xs font-semibold font-[family-name:var(--font-heading)]',
              'pointer-events-none shadow-md',
              'fade-in',
            )}
            aria-hidden="true"
          >
            {ZONE_LABELS[hoveredZone] ?? hoveredZone}
          </div>
        )}
      </div>

      {/* Selected zones chips */}
      {selectedZones.length > 0 && (
        <div
          className="flex flex-wrap gap-2 justify-center w-full max-w-[400px]"
          role="list"
          aria-label="Zone selezionate"
        >
          {selectedZones.map((zoneId) => (
            <button
              key={zoneId}
              type="button"
              onClick={() => handleZoneClick(zoneId)}
              disabled={readOnly}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5',
                'rounded-[var(--radius-full)]',
                'bg-accent-primary-light text-accent-primary',
                'text-sm font-semibold font-[family-name:var(--font-heading)]',
                'transition-all duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                !readOnly && 'hover:bg-accent-primary-medium active:scale-[0.95] cursor-pointer',
                readOnly && 'cursor-default',
              )}
              role="listitem"
              aria-label={
                readOnly
                  ? ZONE_LABELS[zoneId] ?? zoneId
                  : `Rimuovi ${ZONE_LABELS[zoneId] ?? zoneId}`
              }
            >
              <span>{ZONE_LABELS[zoneId] ?? zoneId}</span>
              {!readOnly && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Empty state hint */}
      {selectedZones.length === 0 && !readOnly && (
        <p className="text-sm text-text-muted text-center">
          Tocca le zone del corpo per selezionarle
        </p>
      )}
    </div>
  );
};

BodyMap.displayName = 'BodyMap';
